/**
 * عقد «المساعد» — مطابق لـ`backend/app/routers/assistant.py`.
 *
 *   GET  /assistant/conversation              المحادثة الحالية + الباقي من سقف اليوم
 *   POST /assistant/messages                  رسالة ⇒ رد (وربما كارت تأكيد)
 *   POST /assistant/actions/{id}/confirm      «تمام نفّذ»
 *   POST /assistant/actions/{id}/reject       «لا»
 *   POST /assistant/conversation/new          محادثة جديدة
 *
 * 🔑 الفعل ما بيتنفّذش إلا بالضغطة — والشاشة بتعرض الكارت زي ما السيرفر بعته،
 *    ما بتخترعش نصًّا للفعل.
 */
import { api } from "./client";

export type AssistantAction = { id: string; label_ar: string; tool: string };

export type AssistantResolved = {
  id: string;
  confirmed: boolean;
  ok?: boolean;
  error_ar?: string | null;
};

export type AssistantMessage = {
  role: "user" | "assistant";
  text: string;
  at: string | null;
  action?: AssistantAction;
  resolved?: AssistantResolved;
};

export type AssistantState = {
  enabled: boolean;
  conversationId: string | null;
  messages: AssistantMessage[];
  pendingAction: AssistantAction | null;
  remainingToday: number;
  dailyLimit: number;
};

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function action(raw: unknown): AssistantAction | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const id = str(r["id"]);
  return id ? { id, label_ar: str(r["label_ar"]), tool: str(r["tool"]) } : undefined;
}

function message(raw: unknown): AssistantMessage {
  const r = (raw ?? {}) as Record<string, unknown>;
  const out: AssistantMessage = {
    role: r["role"] === "user" ? "user" : "assistant",
    text: str(r["text"]),
    at: typeof r["at"] === "string" ? r["at"] : null,
  };
  const a = action(r["action"]);
  if (a) out.action = a;
  if (r["resolved"] && typeof r["resolved"] === "object") {
    const x = r["resolved"] as Record<string, unknown>;
    out.resolved = {
      id: str(x["id"]),
      confirmed: Boolean(x["confirmed"]),
      ok: typeof x["ok"] === "boolean" ? x["ok"] : undefined,
      error_ar: typeof x["error_ar"] === "string" ? x["error_ar"] : null,
    };
  }
  return out;
}

function state(raw: unknown): AssistantState {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    enabled: Boolean(r["enabled"]),
    conversationId: typeof r["conversation_id"] === "string" ? r["conversation_id"] : null,
    messages: Array.isArray(r["messages"]) ? (r["messages"] as unknown[]).map(message) : [],
    pendingAction: action(r["pending_action"]) ?? null,
    remainingToday: typeof r["remaining_today"] === "number" ? r["remaining_today"] : 0,
    dailyLimit: typeof r["daily_limit"] === "number" ? r["daily_limit"] : 0,
  };
}

export async function fetchConversation(signal?: AbortSignal): Promise<AssistantState> {
  const res = await api.get<unknown>("/assistant/conversation", signal ? { signal } : {});
  return state(res.data);
}

export async function sendMessage(text: string, context: { path?: string }): Promise<AssistantState> {
  const res = await api.post<unknown>("/assistant/messages", { text, context });
  return state(res.data);
}

export async function confirmAction(id: string): Promise<AssistantState> {
  const res = await api.post<unknown>(`/assistant/actions/${encodeURIComponent(id)}/confirm`);
  return state(res.data);
}

export async function rejectAction(id: string): Promise<AssistantState> {
  const res = await api.post<unknown>(`/assistant/actions/${encodeURIComponent(id)}/reject`);
  return state(res.data);
}

export async function startNewConversation(): Promise<AssistantState> {
  const res = await api.post<unknown>("/assistant/conversation/new");
  return state(res.data);
}

/* ══════════════ طلبات التعديل — للمدير ══════════════ */

export type ChangeRequestStatus = "new" | "planned" | "done" | "rejected";

export type ChangeRequest = {
  id: string;
  title_ar: string;
  details_ar: string;
  status: ChangeRequestStatus;
  status_label_ar: string;
  requested_by: { id: string; name_ar: string };
  dept: string[];
  reply_ar: string | null;
  created_at: string | null;
};

function changeRequest(raw: unknown): ChangeRequest {
  const r = (raw ?? {}) as Record<string, unknown>;
  const by = (r["requested_by"] ?? {}) as Record<string, unknown>;
  const st = str(r["status"]);
  return {
    id: str(r["id"]),
    title_ar: str(r["title_ar"]),
    details_ar: str(r["details_ar"]),
    status: st === "planned" || st === "done" || st === "rejected" ? st : "new",
    status_label_ar: str(r["status_label_ar"]),
    requested_by: { id: str(by["id"]), name_ar: str(by["name_ar"]) },
    dept: Array.isArray(r["dept"]) ? (r["dept"] as unknown[]).map(str) : [],
    reply_ar: typeof r["reply_ar"] === "string" ? r["reply_ar"] : null,
    created_at: typeof r["created_at"] === "string" ? r["created_at"] : null,
  };
}

export async function fetchChangeRequests(status: ChangeRequestStatus | "all" = "all"): Promise<ChangeRequest[]> {
  const res = await api.get<unknown>("/assistant/change-requests", { query: { status } });
  return Array.isArray(res.data) ? (res.data as unknown[]).map(changeRequest) : [];
}

export async function setChangeRequestStatus(
  id: string,
  status: ChangeRequestStatus,
  replyAr: string | null,
): Promise<ChangeRequest> {
  const res = await api.post<unknown>(`/assistant/change-requests/${encodeURIComponent(id)}/status`, {
    status,
    reply_ar: replyAr,
  });
  return changeRequest(res.data);
}
